import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAssets } from '@labradorsports/assets';

import {
    playbookActions,
    Selectors,
    RootState,
    useExportGIFMutation,
    useCheckGIFStatusQuery,
    useLoadEntityLogoQuery,
} from '../../../store/index.js';
import { useDispatcher } from '../../hooks/index.js';

interface Props {
    play: any;
    hovered?: boolean;
    onGIFLoaded?: (playId: string, previewUrl: string, gifUrl: string) => void;
    doNotGenerate?: boolean;
}

const PlayPreview: FC<Props> = ({ play, hovered, onGIFLoaded, doNotGenerate }) => {
    const { LogoImg } = useAssets();

    const addGIFDownload = useDispatcher(playbookActions.AddGIFDownload);
    const removeGIFDownload = useDispatcher(playbookActions.RemoveGIFDownload);
    const [exportGIF] = useExportGIFMutation();

    const gifDownload = useSelector((state: RootState) => Selectors.gifDownload(state, play.id, play.playbookId));
    const [playingPreview, setPlayingPreview] = useState(false);
    const [gifComplete, setGIFComplete] = useState(false);
    const [gifDelay, setGIFDelay] = useState([]);
    const timeoutRef = useRef(null);
    const { data: gifStatus, isFetching } = useCheckGIFStatusQuery(
        {
            playbookId: gifDownload?.playbookId,
            playId: gifDownload?.playId,
            programId: gifDownload?.programId,
            linkId: gifDownload?.linkId,
            gifDelay: gifDelay[0],
        },
        { skip: !gifDownload || gifComplete || typeof gifDelay[0] !== 'number' }
    );

    const { data: entityLogoUrl } = useLoadEntityLogoQuery({ programId: play?.programId });

    useEffect(() => {
        if (gifDownload && !isFetching) {
            const [delay, nextDelay, gifTimeout] = gifDelay;

            if (typeof delay === 'number') {
                timeoutRef.current = setTimeout(async () => {
                    if (!gifStatus?.complete && gifDownload) {
                        const timeLeft = gifTimeout - delay;

                        if (timeLeft > 0) {
                            // Math.random() is a hacky way to change the query params
                            setGIFDelay([nextDelay, Math.max(nextDelay / 2, 2000) + Math.random(), timeLeft]);
                        } else {
                            addGIFDownload({
                                ...gifDownload,
                                error: true,
                            });
                        }
                    } else {
                        setGIFComplete(true);
                    }
                }, delay);
            }
        }
    }, [gifDelay, gifDownload?.playId, isFetching]);

    useEffect(() => {
        setGIFComplete(false);

        if (gifDownload) {
            setGIFDelay([gifDownload.estimatedWait, gifDownload.estimatedWait / 2, 120000]);
        }
    }, [gifDownload?.playId]);

    useEffect(() => {
        if (!gifDownload && !play.previewUrl && !doNotGenerate) {
            exportGIF({ playId: play.id });
        }
    }, [play, gifDownload]);

    useEffect(() => {
        if (
            gifComplete &&
            onGIFLoaded &&
            typeof gifDownload.previewUrl !== 'undefined' &&
            typeof gifDownload.gifUrl !== 'undefined'
        ) {
            onGIFLoaded(play.id, gifDownload.previewUrl, gifDownload.gifUrl);
        }
    }, [gifComplete]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            removeGIFDownload(gifDownload);
        };
    }, []);

    const onHover = () => {
        setPlayingPreview(true);
    };

    const onLeave = () => {
        setPlayingPreview(false);
    };

    if (!play.previewUrl && (!gifComplete || !gifDownload)) {
        return (
            <div className="play-preview p-5">
                <img className="preview-placeholder" src={entityLogoUrl ?? LogoImg} alt="site logo" />
            </div>
        );
    }

    // Use || instead of ?? so we ignore empty string from play props
    const previewUrl = play.previewUrl || gifDownload.previewUrl;
    const gifUrl = play.gifUrl || gifDownload.gifUrl;

    return (
        <div className="play-preview" onMouseEnter={onHover} onMouseLeave={onLeave}>
            <img src={playingPreview || hovered ? gifUrl : previewUrl} alt="Play preview" />
        </div>
    );
};

export default PlayPreview;
